/**
 * Camera maths for the (almost) infinite canvas.
 *
 * Pure functions with no DOM or module dependencies beyond the protocol
 * constants, so they can be unit-tested in Node as well as used in the browser.
 *
 * A camera is `{ x, y, zoom }` where `x`/`y` is the world point sitting at the
 * centre of the viewport, and `zoom` is device pixels per world unit.
 */

import { ZOOM_MIN, ZOOM_MAX, PAN_MARGIN_SCREENS } from './protocol.js';

export const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

export const clampZoom = (zoom) => clamp(zoom, ZOOM_MIN, ZOOM_MAX);

/** World-space rectangle currently on screen. */
export function visibleRect(cam, vp) {
  const halfW = vp.width / (2 * cam.zoom);
  const halfH = vp.height / (2 * cam.zoom);
  return {
    minX: cam.x - halfW,
    minY: cam.y - halfH,
    maxX: cam.x + halfW,
    maxY: cam.y + halfH,
  };
}

export function worldToScreen(cam, vp, wx, wy) {
  return {
    x: (wx - cam.x) * cam.zoom + vp.width / 2,
    y: (wy - cam.y) * cam.zoom + vp.height / 2,
  };
}

export function screenToWorld(cam, vp, sx, sy) {
  return {
    x: (sx - vp.width / 2) / cam.zoom + cam.x,
    y: (sy - vp.height / 2) / cam.zoom + cam.y,
  };
}

/**
 * The region the viewport is allowed to sit inside: everything drawn so far,
 * grown by `PAN_MARGIN_SCREENS` screenfuls of blank space on every side.
 *
 * Because it is derived from the artwork's bounding box, the moment someone
 * draws further out the frontier moves with them — there is always room to keep
 * going, but you can never wander into an empty void and lose the drawing.
 */
export function pannableRegion(content, cam, vp) {
  const box = content ?? { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  const marginX = (vp.width / cam.zoom) * PAN_MARGIN_SCREENS;
  const marginY = (vp.height / cam.zoom) * PAN_MARGIN_SCREENS;
  return {
    minX: box.minX - marginX,
    minY: box.minY - marginY,
    maxX: box.maxX + marginX,
    maxY: box.maxY + marginY,
  };
}

/** Keep the camera centre inside the pannable region. */
export function clampCamera(cam, vp, content) {
  const zoom = clampZoom(cam.zoom);
  const halfW = vp.width / (2 * zoom);
  const halfH = vp.height / (2 * zoom);
  const region = pannableRegion(content, { ...cam, zoom }, vp);

  const minX = region.minX + halfW;
  const maxX = region.maxX - halfW;
  const minY = region.minY + halfH;
  const maxY = region.maxY - halfH;

  return {
    zoom,
    // If the region is narrower than the viewport there is nothing to pan to,
    // so park the camera in the middle of it.
    x: minX > maxX ? (region.minX + region.maxX) / 2 : clamp(cam.x, minX, maxX),
    y: minY > maxY ? (region.minY + region.maxY) / 2 : clamp(cam.y, minY, maxY),
  };
}

/** Zoom while keeping the world point under `(sx, sy)` pinned to that pixel. */
export function zoomAt(cam, vp, sx, sy, factor) {
  const zoom = clampZoom(cam.zoom * factor);
  if (zoom === cam.zoom) return cam;
  const before = screenToWorld(cam, vp, sx, sy);
  const after = screenToWorld({ ...cam, zoom }, vp, sx, sy);
  return { zoom, x: cam.x + (before.x - after.x), y: cam.y + (before.y - after.y) };
}

/** Camera that frames `box` with a little breathing room. */
export function cameraForBounds(box, vp, padding = 0.12) {
  const w = Math.max(1e-6, box.maxX - box.minX);
  const h = Math.max(1e-6, box.maxY - box.minY);
  const zoom = clampZoom(Math.min(
    vp.width / (w * (1 + padding * 2)),
    vp.height / (h * (1 + padding * 2)),
  ));
  return { x: (box.minX + box.maxX) / 2, y: (box.minY + box.maxY) / 2, zoom };
}

/**
 * Camera that reproduces what a peer is looking at.
 *
 * Their zoom is matched, but shrunk if their window is larger than ours so we
 * still see everything they see rather than a cropped middle.
 */
export function cameraForPeerViewport(peer, vp) {
  const fit = Math.min(vp.width / peer.w, vp.height / peer.h, 1);
  return { x: peer.cx, y: peer.cy, zoom: clampZoom(peer.zoom * fit) };
}

export const easeOutCubic = (t) => 1 - (1 - t) ** 3;

/** Interpolate between cameras. Zoom moves geometrically so it feels linear. */
export function lerpCamera(a, b, t) {
  const e = easeOutCubic(clamp(t, 0, 1));
  return {
    x: a.x + (b.x - a.x) * e,
    y: a.y + (b.y - a.y) * e,
    zoom: a.zoom * (b.zoom / a.zoom) ** e,
  };
}

/** True when two cameras are close enough that animating between them is pointless. */
export function camerasClose(a, b, vp) {
  const dx = Math.abs(a.x - b.x) * a.zoom;
  const dy = Math.abs(a.y - b.y) * a.zoom;
  const dz = Math.abs(Math.log(a.zoom / b.zoom));
  return dx < 0.5 && dy < 0.5 && dz < 0.002 && vp.width > 0;
}

/** Grow a box by a multiplier around its centre, with a sensible minimum. */
export function padBounds(box, factor) {
  const w = ((box.maxX - box.minX) * (factor - 1)) / 2;
  const h = ((box.maxY - box.minY) * (factor - 1)) / 2;
  const minPad = Math.max(8, Math.max(box.maxX - box.minX, box.maxY - box.minY) * 0.05);
  return {
    minX: box.minX - Math.max(w, minPad),
    minY: box.minY - Math.max(h, minPad),
    maxX: box.maxX + Math.max(w, minPad),
    maxY: box.maxY + Math.max(h, minPad),
  };
}

export { ZOOM_MIN, ZOOM_MAX, PAN_MARGIN_SCREENS };
